import type { BoardModelKind } from "./kind";
import { labelClassForBoardModel } from "./labelClass";
import { mountBoardModelDebugFrameLabel } from "./frameLabel";
import {
  type BoardModelDebugLabelPlacement,
  labelPlacementClass,
  labelPlacementForBoardModel,
} from "./labelPlacement";
import { positionBoardModelDebugLabel } from "./labelPosition";
import type { Bounds } from "./bounds";

export const BOARD_MODEL_DEBUG_LABEL_HOST_SELECTOR =
  "[data-board-model-debug-label-host]";

export function boardModelDebugLabelHost(
  world: HTMLElement,
): HTMLElement | null {
  return world.querySelector<HTMLElement>(BOARD_MODEL_DEBUG_LABEL_HOST_SELECTOR);
}

/** Label overlay must exist before sync; layout can run in the same turn as React mount. */
export function ensureBoardModelDebugLabelHost(world: HTMLElement): HTMLElement {
  const existing = boardModelDebugLabelHost(world);
  if (existing !== null) {
    return existing;
  }
  const host = document.createElement("div");
  host.className = "timeline-board-debug-label-host";
  host.dataset.boardModelDebugLabelHost = "";
  host.setAttribute("aria-hidden", "true");
  const mount = world.querySelector<HTMLElement>(".timeline-locations")
    ?? world;
  mount.appendChild(host);
  return host;
}

export function ensureBoardModelDebugLabel(
  host: HTMLElement,
  model: BoardModelKind,
  id: string,
  placement?: BoardModelDebugLabelPlacement,
): HTMLElement {
  const selector =
    `[data-board-debug-label][data-board-model="${model}"]`
    + `[data-board-id="${CSS.escape(id)}"]`;
  let label = host.querySelector<HTMLElement>(selector);
  if (label === null) {
    label = document.createElement("span");
    label.dataset.boardDebugLabel = "";
    label.dataset.boardModel = model;
    label.dataset.boardId = id;
    host.appendChild(label);
  }
  const labelClass = labelClassForBoardModel(model);
  const resolvedPlacement = placement ?? labelPlacementForBoardModel(model);
  label.className = [
    "board-model-debug__label",
    labelClass,
    labelPlacementClass(resolvedPlacement),
  ].join(" ");
  mountBoardModelDebugFrameLabel(label, model);
  return label;
}

export function syncBoardModelDebugLabel(
  host: HTMLElement | null,
  model: BoardModelKind,
  id: string,
  box: Bounds | null,
  placement: BoardModelDebugLabelPlacement,
): void {
  if (host === null) {
    return;
  }
  if (box === null) {
    hideBoardModelDebugLabel(host, model, id);
    return;
  }
  const label = ensureBoardModelDebugLabel(host, model, id, placement);
  label.style.display = "";
  positionBoardModelDebugLabel(label, box, placement);
}

export function hideBoardModelDebugLabel(
  host: HTMLElement,
  model: BoardModelKind,
  id: string,
): void {
  const label = host.querySelector<HTMLElement>(
    `[data-board-debug-label][data-board-model="${model}"]`
      + `[data-board-id="${CSS.escape(id)}"]`,
  );
  if (label !== null) {
    label.style.display = "none";
  }
}

export function hideStaleBoardModelDebugLabels(
  host: HTMLElement,
  model: BoardModelKind,
  activeIds: ReadonlySet<string>,
): void {
  for (const label of host.querySelectorAll<HTMLElement>(
    `[data-board-debug-label][data-board-model="${model}"]`,
  )) {
    const id = label.dataset.boardId ?? "";
    if (!activeIds.has(id)) {
      label.style.display = "none";
      continue;
    }
    label.style.display = "";
  }
}

export function stripLegacyFrameLabel(frame: HTMLElement): void {
  frame.querySelector<HTMLElement>(".board-model-debug__label")?.remove();
}
